import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  schema?: object;
  noIndex?: boolean;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  twitterCard = 'summary_large_image',
  schema,
  noIndex = false,
}: SEOProps) => {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://realestatedevelopmentauthority.net';

    // Resolve canonical: use explicit value, fallback to current pathname for indexable pages
    const resolvedCanonical = canonical || (!noIndex ? window.location.pathname : undefined);

    // Update title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Handle robots meta (noindex for internal pages)
    const robotsContent = noIndex ? 'noindex, nofollow' : 'index, follow';
    updateMetaTag('robots', robotsContent);

    // Update canonical (only for indexable pages)
    if (resolvedCanonical && !noIndex) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', `${siteUrl}${resolvedCanonical}`);
    } else if (noIndex) {
      // Remove canonical for noindex pages — no need to signal a preferred URL
      const link = document.querySelector('link[rel="canonical"]');
      if (link) link.remove();
    }

    // Update Open Graph tags
    if (ogTitle) {
      updateMetaTag('og:title', ogTitle, true);
    }

    if (ogDescription) {
      updateMetaTag('og:description', ogDescription, true);
    }

    if (ogImage) {
      updateMetaTag('og:image', ogImage, true);
    }

    if (ogType) {
      updateMetaTag('og:type', ogType, true);
    }

    // Always set og:url for indexable pages so Google sees the correct URL
    if (resolvedCanonical && !noIndex) {
      updateMetaTag('og:url', `${siteUrl}${resolvedCanonical}`, true);
    }

    // Update Twitter Card tags
    updateMetaTag('twitter:card', twitterCard);

    if (ogTitle) {
      updateMetaTag('twitter:title', ogTitle);
    }

    if (ogDescription) {
      updateMetaTag('twitter:description', ogDescription);
    }

    if (ogImage) {
      updateMetaTag('twitter:image', ogImage);
    }

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Add or update Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[data-schema="page"]');

      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        scriptTag.setAttribute('data-schema', 'page');
        document.head.appendChild(scriptTag);
      }

      // Replace placeholder URL with actual site URL
      const schemaString = JSON.stringify(schema, null, 2).replace(
        /"url":\s*"SITE_URL/g,
        `"url": "${siteUrl}`
      );

      scriptTag.textContent = schemaString;
    }

    // Cleanup function
    return () => {
      // Remove page-specific schema when component unmounts
      const scriptTag = document.querySelector('script[data-schema="page"]');
      if (scriptTag) {
        scriptTag.remove();
      }

      // Remove canonical if it still matches this page's canonical,
      // preventing stale canonicals from bleeding into the next page before it sets its own
      if (resolvedCanonical && !noIndex) {
        const link = document.querySelector('link[rel="canonical"]');
        if (link && link.getAttribute('href') === `${siteUrl}${resolvedCanonical}`) {
          link.remove();
        }
        // Also remove og:url to prevent stale value on next page
        const ogUrlMeta = document.querySelector('meta[property="og:url"]');
        if (ogUrlMeta && ogUrlMeta.getAttribute('content') === `${siteUrl}${resolvedCanonical}`) {
          ogUrlMeta.remove();
        }
      }

      // Reset robots to default on unmount
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) {
        robotsMeta.setAttribute('content', 'index, follow');
      }
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogImage, ogType, twitterCard, schema, noIndex]);
};
